import { asLocationId, type LocationId } from "@repo/domain";
import { z } from "zod";
import { addressSchema, contactPointSchema } from "./common.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";
import { codeableConceptSchema } from "./schedule.schemas";

/** FHIR Location hours of operation */
const hoursOfOperationSchema = z.object({
  daysOfWeek: z.array(z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"])).optional(),
  allDay: z.boolean().optional(),
  openingTime: z.string().optional(),
  closingTime: z.string().optional(),
});

export const locationIdSchema: z.ZodType<LocationId, z.ZodTypeDef, string> = z.string().min(1).transform(asLocationId);

/** Create location input */
export const createLocationInputSchema = z.object({
  id: newEntityIdSchema,
  scheduleId: newEntityIdSchema,
  name: z.string().min(1),
  status: z.enum(["active", "suspended", "inactive"]),
  description: z.string().optional(),
  type: z.array(codeableConceptSchema).optional(),
  physicalType: codeableConceptSchema.optional(),
  address: addressSchema.optional(),
  telecom: z.array(contactPointSchema).optional(),
  hoursOfOperation: z.array(hoursOfOperationSchema).optional(),
  partOfId: locationIdSchema.optional(),
});

export type CreateLocationInput = z.infer<typeof createLocationInputSchema>;

/** Update location input */
export const updateLocationInputSchema = z.object({
  id: locationIdSchema,
  name: z.string().min(1).optional(),
  status: z.enum(["active", "suspended", "inactive"]).optional(),
  description: z.string().nullable().optional(),
  type: z.array(codeableConceptSchema).optional(),
  physicalType: codeableConceptSchema.optional(),
  address: addressSchema.optional(),
  telecom: z.array(contactPointSchema).optional(),
  hoursOfOperation: z.array(hoursOfOperationSchema).optional(),
  partOfId: locationIdSchema.nullable().optional(),
});

export type UpdateLocationInput = z.infer<typeof updateLocationInputSchema>;

/** Delete location input */
export const deleteLocationInputSchema = z.object({
  id: locationIdSchema,
});

export type DeleteLocationInput = z.infer<typeof deleteLocationInputSchema>;
